import { PANEL_EVENTS } from '../shell/protocol';
import { useState, useRef, useEffect } from 'react';
import { SelectedToolProps, ToolGroupProps } from './types';

function useToolbar(
    toolGroups: ToolGroupProps[],
    initialSelectedTool?: SelectedToolProps,
    longPressDelay: number = 300,
    direction: 'vertical' | 'horizontal' = 'vertical',
    onToolSelect?: (groupId: string, toolId: string) => void,
    isDragging: boolean = false,
) {
    const [selectedTool, setSelectedTool] = useState<SelectedToolProps>(
        initialSelectedTool || {
            groupId: toolGroups[0].groupId,
            toolId: toolGroups[0].tools[0].id,
        },
    );
    const [activeMenu, setActiveMenu] = useState<string | null>(null);
    const [menuPosition, setMenuPosition] =
        useState<React.CSSProperties | null>(null);
    const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const longPressActivated = useRef(false);
    const activeIconRef = useRef<HTMLElement | null>(null);
    // Icon the open submenu is anchored to; kept separate from activeIconRef,
    // which tracks whatever icon is currently pressed.
    const menuOwnerIconRef = useRef<HTMLElement | null>(null);
    // True when this press started on a different tool while a submenu was
    // open, so the release must run the pressed tool even though the menu
    // state has not flushed yet.
    const dismissedMenuForPress = useRef(false);
    const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

    // Recompute the popup position from the live icon rect so it stays
    // anchored when the window resizes or the page zoom level changes.
    const computeMenuPosition = (
        iconElement: HTMLElement,
    ): React.CSSProperties => {
        const iconRect = iconElement.getBoundingClientRect();
        const menuWidth = 180;
        const menuHeight = 200;
        const margin = 6;
        let top: number;
        let left: number;
        if (direction === 'vertical') {
            top = iconRect.top;
            left = iconRect.right + margin;
            if (left + menuWidth > window.innerWidth - margin) {
                left = iconRect.left - menuWidth - margin;
            }
            if (left < margin) left = margin;
        } else {
            top = iconRect.bottom + margin;
            left = iconRect.left;
        }
        top = Math.min(
            Math.max(margin, top),
            Math.max(margin, window.innerHeight - menuHeight - margin),
        );
        return { top: `${top}px`, left: `${left}px` };
    };

    const closeMenu = () => {
        setActiveMenu(null);
        setMenuPosition(null);
        activeIconRef.current = null;
        menuOwnerIconRef.current = null;
    };

    useEffect(() => {
        setPortalTarget(document.body);
        return () => {
            if (pressTimer.current) clearTimeout(pressTimer.current);
        };
    }, []);

    useEffect(() => {
        const handleOutsideClick = (e: MouseEvent) => {
            if (!activeMenu) return;
            const isOutsideIcon =
                menuOwnerIconRef.current &&
                !menuOwnerIconRef.current.contains(e.target as Node);
            const menuElement = document.querySelector('.tool-menu');
            const isOutsideMenu =
                menuElement && !menuElement.contains(e.target as Node);
            if (isOutsideIcon && isOutsideMenu) {
                closeMenu();
            }
        };
        // A mousedown on a sibling tool must both dismiss the open submenu and
        // run that tool. closeMenu() alone leaves activeMenu stale until the
        // next render, which would make handleMouseUp skip the selection, so
        // remember why the press began.
        const handleToolbarPress = (e: MouseEvent) => {
            if (!activeMenu) return;
            if (
                menuOwnerIconRef.current &&
                menuOwnerIconRef.current.contains(e.target as Node)
            )
                return;
            const menuElement = document.querySelector('.tool-menu');
            if (menuElement && menuElement.contains(e.target as Node)) return;
            dismissedMenuForPress.current = true;
            closeMenu();
        };
        const handleViewportChange = () => {
            if (!activeMenu) return;
            if (
                menuOwnerIconRef.current &&
                document.contains(menuOwnerIconRef.current)
            ) {
                setMenuPosition(computeMenuPosition(menuOwnerIconRef.current));
            } else {
                closeMenu();
            }
        };
        // Any pointerdown anywhere while a drag just began closes an open
        // submenu: drag and long-press are mutually exclusive gestures. The
        // drag sensors capture the initial pointerdown, so reacting to the
        // first mousedown after that is enough.
        const handleDragStartClose = () => {
            if (activeMenu) closeMenu();
            if (pressTimer.current) {
                clearTimeout(pressTimer.current);
                pressTimer.current = null;
            }
            longPressActivated.current = false;
        };
        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('mousedown', handleToolbarPress);
        document.addEventListener(PANEL_EVENTS.toolbarDragActive, handleDragStartClose);
        window.addEventListener('resize', handleViewportChange);
        window.addEventListener('scroll', handleViewportChange, true);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('mousedown', handleToolbarPress);
            document.removeEventListener(
                PANEL_EVENTS.toolbarDragActive,
                handleDragStartClose,
            );
            window.removeEventListener('resize', handleViewportChange);
            window.removeEventListener('scroll', handleViewportChange, true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeMenu]);

    const handleMouseDown = (e: React.MouseEvent, groupId: string) => {
        // Left button only selects/long-presses a tool; right-click has its own
        // semantics (it must never act as a click here)
        if (e.button !== 0) return;
        // A toolbar drag-reorder in progress must never interact with the
        // click/long-press semantics (keeps click+wait and click+drag apart)
        if (isDragging) return;
        longPressActivated.current = false;
        if (pressTimer.current) clearTimeout(pressTimer.current);
        const iconElement = e.currentTarget as HTMLElement;
        activeIconRef.current = iconElement;
        // Track whether this press began on a different tool with a submenu
        // open, so the mouseup selection below is not suppressed.
        if (activeMenu && activeMenu !== groupId) {
            dismissedMenuForPress.current = true;
            menuOwnerIconRef.current = null;
        }
        pressTimer.current = setTimeout(() => {
            if (isDragging) return;
            const group = toolGroups.find((g) => g.groupId === groupId);
            if (group && group.tools.length > 1) {
                longPressActivated.current = true;
                setMenuPosition(computeMenuPosition(iconElement));
                setActiveMenu(groupId);
                menuOwnerIconRef.current = iconElement;
            }
        }, longPressDelay);
        // The press may end without a mouseup on the icon (pointer dragged
        // off the rail); without this cancel the submenu still opens later.
        const cancelLongPress = () => {
            if (pressTimer.current) {
                clearTimeout(pressTimer.current);
                pressTimer.current = null;
            }
        };
        iconElement.addEventListener('mouseleave', cancelLongPress, { once: true });
        window.addEventListener('mouseup', cancelLongPress, { once: true });
    };

    const handleMouseUp = (
        e: React.MouseEvent,
        groupId: string,
        toolId: string,
    ) => {
        if (e.button !== 0) return;
        if (isDragging) return;
        if (pressTimer.current) clearTimeout(pressTimer.current);
        pressTimer.current = null;
        // If this press held long enough to open its own submenu, it counts as
        // a long press even when it began by dismissing a previous menu.
        const wasLongPress = longPressActivated.current;
        const menuWasOpen = dismissedMenuForPress.current;
        longPressActivated.current = false;
        dismissedMenuForPress.current = false;
        if (!wasLongPress && (menuWasOpen || !activeMenu)) {
            selectTool(groupId, toolId);
        }
    };

    const selectTool = (groupId: string, toolId: string) => {
        setSelectedTool({ groupId, toolId });
        closeMenu();
        if (onToolSelect) {
            onToolSelect(groupId, toolId);
        }
    };

    return {
        selectedTool,
        activeMenu,
        menuPosition,
        activeIconRef,
        portalTarget,
        handleMouseDown,
        handleMouseUp,
        selectTool,
    };
}

export default useToolbar;
